import { format } from "date-fns";

/**
 * Date utility functions to standardize date handling across the application
 */

/**
 * Format a date for HTML date inputs (YYYY-MM-DD format)
 */
export const formatForDateInput = (
  date: Date | string | null | undefined,
): string => {
  // Produce stable calendar date (YYYY-MM-DD) independent of local timezone.
  // If input is null/undefined, use "today" in UTC.
  if (date == null) {
    const now = new Date();
    return [
      now.getUTCFullYear(),
      String(now.getUTCMonth() + 1).padStart(2, "0"),
      String(now.getUTCDate()).padStart(2, "0"),
    ].join("-");
  }

  const d = typeof date === "string" ? new Date(date) : date;

  if (!(d instanceof Date) || isNaN(d.getTime())) {
    // Preserve original contract expected by tests: throw on invalid date input
    throw new Error("Invalid date supplied to formatForDateInput");
  }

  return [
    d.getUTCFullYear(),
    String(d.getUTCMonth() + 1).padStart(2, "0"),
    String(d.getUTCDate()).padStart(2, "0"),
  ].join("-");
};

/**
 * Get current date formatted for HTML date inputs
 */
export const getTodayForInput = (): string => {
  return format(new Date(), "yyyy-MM-dd");
};

/**
 * Normalize a date to UTC date-only string (YYYY-MM-DD)
 * Useful for comparing dates without time components
 */
export const normalizeToISO = (date: Date | string): string => {
  const dt = date instanceof Date ? date : new Date(date);
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Parse an ISO date string (YYYY-MM-DD) to Date object
 * Handles UTC parsing consistently
 */
export const parseISODate = (iso: string): Date => {
  // Normalize YYYY-MM-DD (or extended) so that local date parts match the literal string,
  // preventing timezone offset from shifting the calendar day in tests.
  const basicMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (basicMatch) {
    const [, y, m, d] = basicMatch;
    const year = Number(y);
    const month = Number(m) - 1;
    const day = Number(d);
    if (
      !Number.isNaN(year) &&
      !Number.isNaN(month) &&
      !Number.isNaN(day) &&
      month >= 0 &&
      month < 12 &&
      day >= 1 &&
      day <= 31
    ) {
      // Create a date at local midnight corresponding to the intended calendar date
      const utc = new Date(Date.UTC(year, month, day));
      // Shift forward by the timezone offset so that local date shows the same Y-M-D
      utc.setMinutes(utc.getMinutes() + utc.getTimezoneOffset());
      return utc;
    }
  }
  return new Date(iso);
};

/**
 * Convert any date-like value to ISO date string (YYYY-MM-DD) format
 */
export const toISODateString = (
  date: Date | string | null | undefined,
): string | undefined => {
  if (!date) return undefined;
  const dt = date instanceof Date ? date : new Date(date);

  // Check if date is invalid (NaN timestamp)
  if (isNaN(dt.getTime())) return undefined;

  return dt.toISOString().split("T")[0];
};

/**
 * Check if two dates represent the same day (ignoring time)
 */
export const isSameDay = (
  date1: Date | string,
  date2: Date | string,
): boolean => {
  return normalizeToISO(date1) === normalizeToISO(date2);
};

/**
 * Format date for display in payment history and other contexts
 */
export const formatDisplayDate = (date: Date | string): string => {
  let dateObj: Date;
  let dateOnly = false;

  if (typeof date === "string") {
    // If it's a pure date (YYYY-MM-DD), construct in local time to preserve calendar intent
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (m) {
      const [, y, mo, d] = m;
      dateObj = new Date(Number(y), Number(mo) - 1, Number(d));
      dateOnly = true;
    } else {
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
    // Heuristic: a date created from a date-only ISO string will be 00:00:00.000 UTC
    // which in negative offsets displays as previous local day. Treat these as date-only.
    if (
      dateObj.getUTCHours() === 0 &&
      dateObj.getUTCMinutes() === 0 &&
      dateObj.getUTCSeconds() === 0 &&
      dateObj.getUTCMilliseconds() === 0
    ) {
      dateOnly = true;
    }
  }

  if (dateOnly) {
    // Format using UTC components to avoid timezone shifting the calendar day
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const mm = monthNames[dateObj.getUTCMonth()];
    const dd = String(dateObj.getUTCDate()).padStart(2, "0");
    const yyyy = dateObj.getUTCFullYear();
    return `${mm} ${dd}, ${yyyy}`;
  }

  return format(dateObj, "MMM dd, yyyy");
};

/**
 * Common date formatting patterns used across the app
 */
export const DateFormats = {
  INPUT: "yyyy-MM-dd", // For HTML date inputs
  DISPLAY: "MMM dd, yyyy", // For general display
  SHORT: "MMM dd", // For compact display
  ISO: "yyyy-MM-dd", // For API/storage
  FULL: "EEEE, MMMM do, yyyy", // For detailed display
} as const;
